import { useCallback, useEffect, useState } from "react";
import { useAuthStore } from "@/store/authStore";
import { readStoredIds, writeStoredIds } from "@/lib/storedIds";
import { DEFAULT_CARD_FIELD_IDS } from "./opportunityCardFields";

const CARD_FIELDS_KEY_PREFIX = "forgepoint.opportunityCardFields.";

interface OpportunityCardFields {
  visibleFieldIds: string[];
  toggleField: (fieldId: string) => void;
}

/**
 * Tracks which fields the signed-in user shows on opportunity cards, persisted
 * to localStorage under a per-user key.
 * @returns The visible field ids and a handler to toggle one
 */
export function useOpportunityCardFields(): OpportunityCardFields {
  const userId = useAuthStore((state) => state.user?.id);
  const storageKey = userId ? `${CARD_FIELDS_KEY_PREFIX}${userId}` : null;
  const [visibleFieldIds, setVisibleFieldIds] = useState<string[]>(
    DEFAULT_CARD_FIELD_IDS,
  );

  useEffect(() => {
    setVisibleFieldIds(
      storageKey
        ? (readStoredIds(storageKey) ?? DEFAULT_CARD_FIELD_IDS)
        : DEFAULT_CARD_FIELD_IDS,
    );
  }, [storageKey]);

  const toggleField = useCallback(
    (fieldId: string) => {
      if (!storageKey) {
        return;
      }
      const next = visibleFieldIds.includes(fieldId)
        ? visibleFieldIds.filter((id) => id !== fieldId)
        : [...visibleFieldIds, fieldId];
      writeStoredIds(storageKey, next);
      setVisibleFieldIds(next);
    },
    [visibleFieldIds, storageKey],
  );

  return { visibleFieldIds, toggleField };
}
